import { useMemo, useState } from 'react'
import { Button, Group, Stack, Stepper, Text } from '@mantine/core'

import { Callout, DataCard } from '@/components/ui'
import { useEscopoDoMeuRobo, type EscopoDoRobo as EscopoLido, type Robo } from '@/api/robo'
import { EscopoDoRobo } from '../EscopoDoRobo'
import { AvataresDoRobo } from '../MeuRoboAparencia'
import { FontesDoRobo } from '../MeuRoboNotificacoes'
import { NomeDoMeuRobo } from '../NomeDoMeuRobo'
import { PassoRecebimento } from './PassoRecebimento'
import { PassoRede } from './PassoRede'
import { PASSOS, gravarVisitados, lerVisitados, primeiroPendente, type Passo } from './passos'

const ROTULO: Record<Passo, string> = {
  recebimento: 'Recebimento',
  rede: 'Rede',
  nome: 'Nome',
  escopo: 'O que ele acompanha',
  alertas: 'Alertas',
  aparencia: 'Aparência',
}

/** "de {escopo}" na frase do fim, a partir do que o dono gravou. */
export function descreverEscopo(escopo: EscopoLido | undefined): string {
  const org = escopo?.organizacao_nome ?? 'sua organização'
  const g = escopo?.gravado
  if (!g) return org
  if (g.modo === 'todos') return `${org} inteira`
  const n = g.tenant_ids.length
  return `${n} ${n === 1 ? 'tenant' : 'tenants'} de ${org}`
}

/**
 * O wizard de recebimento (spec 2026-09-27, 4.4): seis passos, retomando do
 * primeiro pendente. Os obrigatórios (recebimento, nome, escopo) vêm da API;
 * os opcionais (rede, alertas, aparência) ficam como vistos na sessão.
 */
export function WizardDeRecebimento({ robo, aoConcluir }: { robo: Robo; aoConcluir: () => void }) {
  const rec = robo.recebimento ?? { estado: null, pendencias: [], atribuido_por: null, atribuido_em: null }
  const [visitados, setVisitados] = useState(() => lerVisitados(robo.public_id))
  const [ativo, setAtivo] = useState<Passo>(() => primeiroPendente(rec, visitados))
  const [pronto, setPronto] = useState(false)
  const { data: escopo } = useEscopoDoMeuRobo()

  const pendentes = useMemo(() => new Set<string>(rec.pendencias), [rec.pendencias])
  const recebimentoPendente = pendentes.has('recebimento')
  const indice = PASSOS.indexOf(ativo)

  const marcar = (passo: Passo) => {
    const novo = new Set(visitados).add(passo)
    setVisitados(novo)
    gravarVisitados(robo.public_id, novo)
  }
  const avancar = (de: Passo) => {
    marcar(de)
    const proximo = PASSOS[PASSOS.indexOf(de) + 1]
    if (proximo) setAtivo(proximo)
  }
  // Passo feito: obrigatório sem pendência, ou opcional visto. É o que deixa clicar nele.
  const feito = (p: Passo) =>
    p === 'recebimento' || p === 'nome' || p === 'escopo' ? !pendentes.has(p) : visitados.has(p)
  // Enquanto o recebimento não é confirmado, nada além do passo 1 se abre.
  const podeAbrir = (p: Passo) => !recebimentoPendente || p === 'recebimento'

  if (pronto) {
    return (
      <DataCard title="Pronto">
        <Stack gap="md" align="flex-start">
          <Text size="sm">Pronto. O {robo.name} já mostra os alertas de {descreverEscopo(escopo)}.</Text>
          <Button onClick={aoConcluir}>Ir para Meu robô</Button>
        </Stack>
      </DataCard>
    )
  }

  const rodape = (passo: Passo, opcoes: { pular?: boolean; bloqueado?: boolean; concluir?: boolean } = {}) => (
    <Group gap="sm" mt="md">
      {opcoes.concluir ? (
        <Button onClick={() => { marcar(passo); setPronto(true) }}>Concluir</Button>
      ) : (
        <Button disabled={opcoes.bloqueado} onClick={() => avancar(passo)}>Continuar</Button>
      )}
      {opcoes.pular && (
        <Button variant="subtle" onClick={() => avancar(passo)}>Faço isso depois</Button>
      )}
    </Group>
  )

  return (
    <DataCard title="Receber e configurar o seu robô">
      <Stepper
        active={indice}
        onStepClick={(i) => { const p = PASSOS[i]; if (p && podeAbrir(p) && (feito(p) || i <= indice)) setAtivo(p) }}
        size="sm"
      >
        <Stepper.Step label={ROTULO.recebimento}>
          <PassoRecebimento robo={robo} aoConfirmar={() => avancar('recebimento')} />
        </Stepper.Step>

        <Stepper.Step label={ROTULO.rede} allowStepSelect={podeAbrir('rede') && feito('rede')}>
          <PassoRede robo={robo} aoConfigurar={() => marcar('rede')} />
          {rodape('rede', { pular: true })}
        </Stepper.Step>

        <Stepper.Step label={ROTULO.nome} allowStepSelect={podeAbrir('nome') && feito('nome')}>
          <Stack gap="sm">
            <Text size="sm" c="dimmed">O chat com o robô abre quando ele tem nome.</Text>
            <NomeDoMeuRobo robo={robo} />
          </Stack>
          {rodape('nome', { bloqueado: pendentes.has('nome') })}
        </Stepper.Step>

        <Stepper.Step label={ROTULO.escopo} allowStepSelect={podeAbrir('escopo') && feito('escopo')}>
          <EscopoDoRobo explicar rota={robo.rota ?? null} tenantDoCadastro={robo.tenant_name}
            modelo={robo.hardware_model} />
          {rodape('escopo', { bloqueado: pendentes.has('escopo') })}
        </Stepper.Step>

        <Stepper.Step label={ROTULO.alertas} allowStepSelect={podeAbrir('alertas') && feito('alertas')}>
          <Callout variant="info" title="O robô não cria alerta novo">
            Ele mostra no display o que você já recebe no Console, filtrado pelo
            que ele acompanha e por esta lista.
          </Callout>
          <FontesDoRobo robo={robo} />
          {rodape('alertas')}
        </Stepper.Step>

        <Stepper.Step label={ROTULO.aparencia} allowStepSelect={podeAbrir('aparencia') && feito('aparencia')}>
          <AvataresDoRobo robo={robo} />
          {rodape('aparencia', { concluir: true })}
        </Stepper.Step>
      </Stepper>
    </DataCard>
  )
}
